import {
    useContext,
    useMemo,
    type FC
} from "react";
import {
    View
} from "react-native";
import type IStatTileProps from "./type";
import stylesheet, {
    useStyles
} from "./stylesheet";
import StatTileDelta from "./components/delta";
import StatTileTitle from "./components/title";
import StatTileValue from "./components/value";
import {
    StatTileContext
} from "./context";
import {
    NCoreUIKitTheme
} from "../../core/hooks";
import {
    CardContext
} from "../card/context";
import Sparkline from "../sparkline";
import Skeleton from "../skeleton";
import Card from "../card";

const StatTileBody: FC<Pick<IStatTileProps, "customTheme" | "children" | "isTrendFlush" | "trendStep" | "trend" | "isAnimatedOnMount">> = ({
    isTrendFlush = false,
    isAnimatedOnMount,
    customTheme,
    trendStep,
    children,
    trend
}) => {
    const cardContext = useContext(CardContext);

    const statContext = useContext(StatTileContext);

    const {
        controlHeights,
        shapeConfigs,
        radiuses,
        borders,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        trendLine: trendLineDynamicStyle,
        trend: trendDynamicStyle,
        stat: statDynamicStyle
    } = useStyles({
        inset: cardContext?.inset ?? spaces.spacingMd,
        isTrendFlush,
        shapeConfigs,
        radiuses,
        borders,
        spaces
    });

    return <>
        <View
            style={[
                stylesheet.stat,
                statDynamicStyle
            ]}
        >
            {children}
        </View>
        {
            trend?.length ?
                <View
                    style={[
                        stylesheet.trend,
                        trendDynamicStyle
                    ]}
                >
                    {
                        statContext?.isLoading ?
                            <Skeleton
                                height={controlHeights.medium}
                                customTheme={customTheme}
                                radius={radiuses.xs}
                            />
                        :
                            <View
                                style={trendLineDynamicStyle}
                            >
                                <Sparkline
                                    isAnimatedOnMount={isAnimatedOnMount}
                                    sequentialStep={trendStep}
                                    customTheme={customTheme}
                                    isDecorative={true}
                                    values={trend}
                                    size="medium"
                                />
                            </View>
                    }
                </View>
            :
                null
        }
    </>;
};

const StatTile: FC<IStatTileProps> = ({
    isAnimatedOnMount,
    isLoading = false,
    customLocalize,
    isTrendFlush,
    customTheme,
    trendStep,
    children,
    trend,
    ...props
}) => {
    const statContext = useMemo(() => ({
        customLocalize,
        customTheme,
        isLoading
    }), [
        customLocalize,
        customTheme,
        isLoading
    ]);

    const body = <StatTileBody
        isAnimatedOnMount={isAnimatedOnMount}
        isTrendFlush={isTrendFlush}
        customTheme={customTheme}
        trendStep={trendStep}
        trend={trend}
    >
        {children}
    </StatTileBody>;

    return <StatTileContext.Provider
        value={statContext}
    >
        <Card
            {...props}
            customLocalize={customLocalize}
            customTheme={customTheme}
            isLoading={isLoading}
            size="small"
        >
            <Card.Content
                placeholder={body}
            >
                {body}
            </Card.Content>
        </Card>
    </StatTileContext.Provider>;
};
export default Object.assign(StatTile, {
    Title: StatTileTitle,
    Value: StatTileValue,
    Delta: StatTileDelta
});
